import React, { createContext, useContext, useState, useEffect, useCallback } from "react";
import { api } from "../services/api";
import { toast } from "sonner";
import { useAuth } from "./AuthContext";

const CartContext = createContext(null);

export const CartProvider = ({ children }) => {
  const { user } = useAuth();
  const [cart, setCart] = useState({
    items: [],
    item_count: 0,
    subtotal_in_cents: 0,
    formatted_subtotal: "R$ 0,00",
    free_shipping_remaining_in_cents: 29900,
  });
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  // Initialize guest cart id if not present
  useEffect(() => {
    let guestId = localStorage.getItem("guest_cart_id");
    if (!guestId) {
      guestId = "guest_" + Math.random().toString(36).substring(2, 12);
      localStorage.setItem("guest_cart_id", guestId);
    }
  }, []);

  const fetchCart = useCallback(async () => {
    try {
      const res = await api.get("/cart");
      if (res.data) {
        setCart(res.data);
      }
    } catch (e) {
      console.error("Erro ao carregar carrinho:", e);
    }
  }, []);

  // Fetch cart on mount and when user status changes
  useEffect(() => {
    fetchCart();
  }, [user, fetchCart]);

  const addToCart = async (productId, quantity = 1) => {
    setLoading(true);
    try {
      await api.post("/cart/items", {
        product_id: productId,
        quantity,
      });
      await fetchCart();
      setIsOpen(true);
      toast.success("Item adicionado ao carrinho!");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Não foi possível adicionar o produto agora.");
    } finally {
      setLoading(false);
    }
  };

  const updateQuantity = async (productId, quantity) => {
    try {
      await api.put(`/cart/items/${productId}`, {
        product_id: productId,
        quantity,
      });
      await fetchCart();
    } catch (err) {
      toast.error("Não foi possível atualizar a quantidade.");
    }
  };

  const removeItem = async (productId) => {
    try {
      await api.delete(`/cart/items/${productId}`);
      await fetchCart();
      toast.info("Item removido do carrinho.");
    } catch (err) {
      toast.error("Erro ao remover item.");
    }
  };

  const clearCart = async () => {
    try {
      await api.delete("/cart");
      await fetchCart();
    } catch (err) {
      console.error(err);
    }
  };

  return (
    <CartContext.Provider
      value={{
        cart,
        isOpen,
        setIsOpen,
        loading,
        addToCart,
        updateQuantity,
        removeItem,
        clearCart,
        fetchCart,
      }}
    >
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => useContext(CartContext);
